import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { planService } from '../services/api';
import styled from 'styled-components';
import { Check, Crown, Star, Zap, ArrowLeft } from 'lucide-react';
import { Plan } from '../types';

const PlansContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #00d4aa 0%, #00b894 100%);
  padding: 40px 20px;
`;

const Header = styled.header`
  max-width: 1200px;
  margin: 0 auto 40px;
  text-align: center;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 32px;
  transition: background-color 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const Title = styled.h1`
  font-size: 48px;
  color: white;
  margin-bottom: 16px;
  font-weight: 700;
`;

const Subtitle = styled.p`
  font-size: 20px;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 32px;
`;

const PlansGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 32px;
`;

const PlanCard = styled.div<{ featured?: boolean; isCurrentPlan?: boolean }>`
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  position: relative;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border: ${props => props.featured ? '3px solid #00d4aa' : props.isCurrentPlan ? '3px solid #28a745' : '1px solid #e9ecef'};
  transform: ${props => props.featured ? 'scale(1.05)' : 'scale(1)'};

  &:hover {
    transform: ${props => props.featured ? 'scale(1.07)' : 'scale(1.02)'};
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  }
`;

const FeaturedBadge = styled.div`
  position: absolute;
  top: -16px;
  left: 50%;
  transform: translateX(-50%);
  background: #00d4aa;
  color: white;
  padding: 8px 24px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CurrentPlanBadge = styled.div`
  position: absolute;
  top: -16px;
  left: 50%;
  transform: translateX(-50%);
  background: #28a745;
  color: white;
  padding: 8px 24px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const PlanIcon = styled.div<{ color: string }>`
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: ${props => props.color}20;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.color};
  margin-bottom: 24px;
`;

const PlanName = styled.h3`
  font-size: 24px;
  color: #333;
  margin-bottom: 8px;
  font-weight: 700;
`;

const PlanDescription = styled.p`
  color: #666;
  margin-bottom: 24px;
  line-height: 1.6;
`;

const PlanPrice = styled.div`
  margin-bottom: 32px;
`;

const Price = styled.div`
  font-size: 48px;
  color: #333;
  font-weight: 700;
  margin-bottom: 8px;
`;

const PriceUnit = styled.span`
  font-size: 16px;
  color: #666;
  font-weight: 400;
`;

const FeaturesList = styled.ul`
  list-style: none;
  margin-bottom: 32px;
`;

const FeatureItem = styled.li<{ available: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  color: ${props => props.available ? '#333' : '#999'};
  text-decoration: ${props => props.available ? 'none' : 'line-through'};
`;

const FeatureIcon = styled.div<{ available: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${props => props.available ? '#28a745' : '#dee2e6'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
`;

const SelectButton = styled.button<{ $variant: 'primary' | 'secondary' | 'success' }>`
  width: 100%;
  padding: 16px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  transition: all 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  ${props => {
    switch (props.$variant) {
      case 'primary':
        return `
          background: #00d4aa;
          color: white;
          &:hover {
            background: #00b894;
          }
        `;
      case 'success':
        return `
          background: #28a745;
          color: white;
          cursor: default;
        `;
      case 'secondary':
      default:
        return `
          background: #f8f9fa;
          color: #666;
          border: 2px solid #e9ecef;
          &:hover {
            background: #e9ecef;
          }
        `;
    }
  }}
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  color: white;
  font-size: 18px;
`;

const ErrorContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  background: rgba(220, 53, 69, 0.1);
  border: 1px solid rgba(220, 53, 69, 0.3);
  border-radius: 8px;
  padding: 24px;
  text-align: center;
  color: white;
`;

export const PlansPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const response = await planService.getPlans();
        setPlans(response.data || []);
      } catch (err) {
        console.error('Erro ao carregar planos:', err);
        setError('Erro ao carregar os planos. Tente novamente mais tarde.');
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const getPlanIcon = (planName: string) => {
    const name = planName.toLowerCase();
    if (name.includes('básico') || name.includes('starter')) {
      return <Star size={32} />;
    } else if (name.includes('profissional') || name.includes('pro')) {
      return <Zap size={32} />;
    } else if (name.includes('premium') || name.includes('enterprise')) {
      return <Crown size={32} />;
    }
    return <Star size={32} />;
  };

  const getPlanColor = (planName: string) => {
    const name = planName.toLowerCase();
    if (name.includes('básico') || name.includes('starter')) {
      return '#28a745';
    } else if (name.includes('profissional') || name.includes('pro')) {
      return '#00d4aa';
    } else if (name.includes('premium') || name.includes('enterprise')) {
      return '#6f42c1';
    }
    return '#00d4aa';
  };

  const isFeatured = (plan: Plan) => {
    return plan.name.toLowerCase().includes('profissional') || 
           plan.name.toLowerCase().includes('pro');
  };

  const isCurrentPlan = (plan: Plan) => {
    // Verifica se o plano é o atual através da primeira loja do usuário
    return user?.stores?.[0]?.plan_id === plan.id;
  };

  const handleSelectPlan = (plan: Plan) => {
    if (isCurrentPlan(plan)) return;

    const planSlug = plan.name
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    if (user) {
      const hasCompanyContext = Boolean(user.stores && user.stores.length > 0);
      navigate(hasCompanyContext ? '/billing' : `/contract/${planSlug}`);
      return;
    }

    navigate(`/app/login?redirect=${encodeURIComponent(`/contract/${planSlug}`)}`);
  };

  const formatPrice = (price: number) => {
    if (price === 0) {
      return 'Grátis';
    }
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  const getFeaturesList = (plan: Plan) => {
    const baseFeatures = [
      plan.max_stores === -1 ? 'Lojas ilimitadas' : `Até ${plan.max_stores} lojas`,
      plan.max_employees_per_store === -1 ? 'Funcionários ilimitados por loja' : `Até ${plan.max_employees_per_store} funcionários por loja`,
      plan.max_customers === -1 ? 'Clientes ilimitados' : `Até ${plan.max_customers.toLocaleString('pt-BR')} clientes`,
      plan.max_rewards === -1 ? 'Recompensas ilimitadas' : `Até ${plan.max_rewards} recompensas`,
    ];

    const dynamicFeatures = [
      ...(plan.features || []),
      ...(plan.has_advanced_reports ? ['Relatórios avançados'] : []),
      ...(plan.has_api_access ? ['API personalizada'] : []),
      ...(plan.has_crm ? ['CRM habilitado'] : []),
      ...(plan.has_custom_smtp ? ['SMTP personalizado'] : []),
      ...(plan.has_tier_program ? ['Programa de níveis (tiers)'] : []),
      ...(plan.has_customer_automation ? ['Automações por comportamento do cliente'] : []),
      ...(plan.has_partner_network ? ['Rede de parceiros para earn/burn'] : []),
      ...(plan.has_ai_insights ? ['Insights com IA (churn/CLV/NBO)'] : []),
      ...(plan.has_priority_support ? ['Suporte prioritário'] : []),
    ];

    const uniqueFeatures = Array.from(new Set([...baseFeatures, ...dynamicFeatures]));

    return uniqueFeatures.map((feature) => ({
      name: feature,
      available: true,
    }));
  };

  if (loading) {
    return (
      <PlansContainer>
        <LoadingContainer>
          Carregando planos...
        </LoadingContainer>
      </PlansContainer>
    );
  }

  if (error) {
    return (
      <PlansContainer>
        <Header>
          <BackButton onClick={() => window.history.back()}>
            <ArrowLeft size={16} />
            Voltar
          </BackButton>
          <ErrorContainer>
            <h3>Ops! Algo deu errado</h3>
            <p>{error}</p>
          </ErrorContainer>
        </Header>
      </PlansContainer>
    );
  }

  return (
    <PlansContainer>
      <Header>
        <BackButton onClick={() => window.history.back()}>
          <ArrowLeft size={16} />
          Voltar
        </BackButton>
        <Title>Escolha seu Plano</Title>
        <Subtitle>
          Compare recursos avançados de fidelização e escolha o melhor plano para escalar recompra e retenção
        </Subtitle>
      </Header>

      <PlansGrid>
        {plans.map((plan) => {
          const featuredPlan = isFeatured(plan);
          const currentPlan = isCurrentPlan(plan);
          const featuresList = getFeaturesList(plan);

          return (
            <PlanCard
              key={plan.id}
              featured={featuredPlan}
              isCurrentPlan={currentPlan}
            >
              {featuredPlan && !currentPlan && (
                <FeaturedBadge>Mais Popular</FeaturedBadge>
              )}
              {currentPlan && (
                <CurrentPlanBadge>Plano Atual</CurrentPlanBadge>
              )}

              <PlanIcon color={getPlanColor(plan.name)}>
                {getPlanIcon(plan.name)}
              </PlanIcon>

              <PlanName>{plan.name}</PlanName>
              <PlanDescription>{plan.description}</PlanDescription>

              <PlanPrice>
                <Price>
                  {formatPrice(plan.price)}
                  {plan.price > 0 && <PriceUnit>/mês</PriceUnit>}
                </Price>
              </PlanPrice>

              <FeaturesList>
                {featuresList.map((feature, index) => (
                  <FeatureItem key={index} available={feature.available}>
                    <FeatureIcon available={feature.available}>
                      {feature.available && <Check size={12} />}
                    </FeatureIcon>
                    {feature.name}
                  </FeatureItem>
                ))}
              </FeaturesList>

              <SelectButton
                $variant={currentPlan ? 'success' : featuredPlan ? 'primary' : 'secondary'}
                onClick={() => handleSelectPlan(plan)}
              >
                {currentPlan ? 'Plano Atual' : 'Selecionar Plano'}
              </SelectButton>
            </PlanCard>
          );
        })}
      </PlansGrid>
    </PlansContainer>
  );
};
